// Mapping screen (T05): the user confirms or corrects which column holds which
// field. Each change is re-run in the import worker, and the summary, issues and
// preview update when it replies.
import {
  FIELDS,
  type ColumnMapping,
  type FieldDef,
  type FieldId,
  type MappingOptions,
} from '../../import/fields';
import type { ImportSettings } from '../../import/normalise';
import type { IssueSample, NormaliseSummary } from '../../import/summary';

interface Props {
  headers: string[];
  settings: ImportSettings;
  summary: NormaliseSummary;
  /** True while the worker is re-running the import after a change. */
  updating: boolean;
  /** Set only when a verified vendor layout matched. */
  vendorName?: string;
  onChange(settings: ImportSettings): void;
  onContinue(): void;
  onBack(): void;
}

export function MappingStep({
  headers,
  settings,
  summary,
  updating,
  vendorName,
  onChange,
  onContinue,
  onBack,
}: Props) {
  const setColumn = (field: FieldId, value: string) => {
    const mapping: ColumnMapping = Object.fromEntries(
      Object.entries(settings.mapping).filter(([key]) => key !== field),
    );
    if (value !== '') mapping[field] = Number(value);
    onChange({ ...settings, mapping });
  };
  const setOption = <K extends keyof MappingOptions>(key: K, value: MappingOptions[K]) =>
    onChange({ ...settings, options: { ...settings.options, [key]: value } });

  return (
    <section aria-labelledby="mapping-heading" aria-busy={updating}>
      <h2 id="mapping-heading">Check your columns</h2>
      <p>
        {vendorName
          ? `We recognised your ${vendorName} export and filled in the columns. Change anything that's wrong.`
          : "We've guessed which column is which. Change anything that's wrong."}
      </p>

      {summary.missingMessage && <p role="alert">{summary.missingMessage}</p>}

      <ColumnGroup
        legend="The columns we need"
        hint="A name or a staff ID, plus the date and times of each shift."
        fields={FIELDS.filter((f) => CORE.includes(f.id))}
        headers={headers}
        settings={settings}
        setColumn={setColumn}
      />
      <ColumnGroup
        legend="Columns that help us check more"
        hint="Breaks, dates of birth and sites make the report more complete. Leave any you don't have."
        fields={FIELDS.filter((f) => !CORE.includes(f.id))}
        headers={headers}
        settings={settings}
        setColumn={setColumn}
      />

      <fieldset>
        <legend>Formats</legend>
        <label>
          Dates like 03/04/2026 are
          <select
            value={settings.options.dateOrder}
            onChange={(e) =>
              setOption('dateOrder', e.currentTarget.value as MappingOptions['dateOrder'])
            }
          >
            <option value="dmy">day first (DD/MM/YYYY)</option>
            <option value="mdy">month first (MM/DD/YYYY)</option>
          </select>
        </label>
        {settings.mapping.break !== undefined && (
          <label>
            Break numbers are in
            <select
              value={settings.options.breakUnit}
              onChange={(e) =>
                setOption('breakUnit', e.currentTarget.value as MappingOptions['breakUnit'])
              }
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
            </select>
          </label>
        )}
      </fieldset>

      <p role="status">{updating ? 'Updating…' : summaryText(summary)}</p>
      <IssueList title="Rows we couldn't use" sample={summary.errors} />
      <IssueList title="Things to check" sample={summary.warnings} />
      <Preview summary={summary} />

      <div class="actions">
        <button type="button" onClick={onBack}>
          Choose a different file
        </button>
        <button type="button" disabled={updating || !summary.canContinue} onClick={onContinue}>
          See the report
        </button>
      </div>
    </section>
  );
}

const CORE: FieldId[] = ['name', 'ref', 'date', 'start', 'end'];

function ColumnGroup({
  legend,
  hint,
  fields,
  headers,
  settings,
  setColumn,
}: {
  legend: string;
  hint: string;
  fields: FieldDef[];
  headers: string[];
  settings: ImportSettings;
  setColumn(field: FieldId, value: string): void;
}) {
  return (
    <fieldset>
      <legend>{legend}</legend>
      <p class="muted">{hint}</p>
      <div class="field-grid">
        {fields.map((field) => (
          <label key={field.id}>
            {field.label}
            {field.hint && <small> {field.hint}</small>}
            <select
              value={settings.mapping[field.id] ?? ''}
              onChange={(e) => setColumn(field.id, e.currentTarget.value)}
            >
              <option value="">Not in my file</option>
              {headers.map((header, index) => (
                <option key={index} value={index}>
                  {columnLetter(index)}: {header || '(no heading)'}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function summaryText(summary: NormaliseSummary): string {
  if (summary.missingFields.length > 0) return '';
  const leftOut = summary.excluded.open + summary.excluded.draft;
  return (
    `Found ${plural(summary.shiftCount, 'shift')} for ${plural(summary.employeeCount, 'person', 'people')}.` +
    (leftOut > 0
      ? ` We've left out ${plural(leftOut, 'open, unassigned or draft shift')}, which we don't check.`
      : '')
  );
}

function IssueList({ title, sample }: { title: string; sample: IssueSample }) {
  if (sample.count === 0) return null;
  return (
    <details open={sample.count <= 5}>
      <summary>
        {title} ({sample.count})
      </summary>
      <ul>
        {sample.first.map((issue, i) => (
          <li key={i}>{issue.message}</li>
        ))}
      </ul>
      {sample.count > sample.first.length && <p>…and {sample.count - sample.first.length} more.</p>}
    </details>
  );
}

function Preview({ summary }: { summary: NormaliseSummary }) {
  if (summary.preview.length === 0) return null;
  return (
    <div class="table-scroll">
      <table>
        <caption>First {summary.preview.length} shifts, as we've read them</caption>
        <thead>
          <tr>
            <th scope="col">Row</th>
            <th scope="col">Person</th>
            <th scope="col">Site</th>
            <th scope="col">Start</th>
            <th scope="col">End</th>
            <th scope="col">Break</th>
          </tr>
        </thead>
        <tbody>
          {summary.preview.map((row) => (
            <tr key={row.sourceRow}>
              <td>{row.sourceRow}</td>
              <td>{row.person}</td>
              <td>{row.site ?? '–'}</td>
              <td>{row.start}</td>
              <td>{row.end}</td>
              <td>{row.breakMinutes === undefined ? 'Not shown' : `${row.breakMinutes} min`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Spreadsheet column letter: 0 → A, 25 → Z, 26 → AA. */
export function columnLetter(index: number): string {
  let n = index + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
